import type {
	UploadBatchSelection,
	UploadProgressMessage,
	UploadProgressMessageV2,
	UploadProgressStage,
} from 'interfaces/app';
import {
	getUploadProgressIdentityKey,
	isUploadProgressMessageV2,
} from './external-upload-helpers';

export type UploadLifecycleTransition =
	| 'started'
	| 'transfer_completed'
	| 'processing_completed'
	| 'completed'
	| 'failed'
	| 'recovering'
	| 'recovered'
	| 'stalled'
	| 'abandoned';

export type UploadLifecycleProperties = {
	appVersion: string;
	batchId: string;
	elapsedStageMs: number;
	elapsedTotalMs: number;
	failureReason?: UploadProgressMessageV2['failureReason'];
	newCount: number;
	platform: string;
	processedCount: number;
	protocolVersion: 2;
	recoveryCount: number;
	rejectedCount: number;
	selectedTotal: number;
	skippedExistingCount: number;
	source: UploadProgressMessageV2['source'];
	stage: UploadProgressStage;
	submittedCount: number;
	transferredCount: number;
	transition: UploadLifecycleTransition;
	uploadSessionId: string;
};

export type UploadLifecycleTrackerOptions = {
	appVersion: string;
	getSamplingRate: () => number;
	now?: () => number;
	platform: () => string;
	track: (properties: UploadLifecycleProperties) => void;
};

type UploadLifecycleState = {
	emitted: Set<UploadLifecycleTransition>;
	lastStage: UploadProgressStage;
	recoveryCount: number;
	sampled: boolean;
	selection: UploadBatchSelection;
	stageStartedAt: number;
	totalStartedAt: number;
};

const MAX_TERMINAL_UPLOAD_BATCHES = 32;

const alwaysTrackTransitions: ReadonlySet<UploadLifecycleTransition> = new Set([
	'abandoned',
	'failed',
	'recovered',
	'recovering',
	'stalled',
]);

function getTransitionsProvenByStage(
	stage: UploadProgressStage,
): UploadLifecycleTransition[] {
	const transitions: UploadLifecycleTransition[] = ['started'];

	if (
		stage === 'processing'
		|| stage === 'saving'
		|| stage === 'completed'
	) {
		transitions.push('transfer_completed');
	}

	if (stage === 'saving' || stage === 'completed') {
		transitions.push('processing_completed');
	}

	if (stage === 'completed') {
		transitions.push('completed');
	} else if (stage === 'failed') {
		transitions.push('failed');
	} else if (stage === 'aborted') {
		transitions.push('abandoned');
	}

	return transitions;
}

/**
 * Deterministically selects all lifecycle events for one batch together.
 *
 * @param batchId Random batch correlation identifier.
 * @param samplingRate Inclusive sampling rate in the range `[0, 1]`.
 * @returns Whether successful lifecycle transitions for the batch are sampled.
 */
export function shouldSampleUploadBatch(
	batchId: string,
	samplingRate: number,
): boolean {
	const normalizedRate = Math.min(
		1,
		Math.max(
			0,
			Number.isFinite(samplingRate) ? samplingRate : 0,
		),
	);

	if (normalizedRate === 0) {
		return false;
	}

	if (normalizedRate === 1) {
		return true;
	}

	let hash = 2_166_136_261;

	for (let index = 0; index < batchId.length; index += 1) {
		hash ^= batchId.charCodeAt(index);
		hash = Math.imul(
			hash,
			16_777_619,
		);
	}

	return (hash >>> 0) / 4_294_967_296 < normalizedRate;
}

/** Tracks stage transitions once per correlated batch without tick-level noise. */
export class UploadLifecycleTracker {
	private readonly states = new Map<string, UploadLifecycleState>();

	private readonly terminalBatchIdentities = new Set<string>();

	public constructor(private readonly options: UploadLifecycleTrackerOptions) {}

	/**
	 * Observes a validated progress payload and emits any newly proven lifecycle
	 * transitions. Legacy payloads are ignored because they lack safe correlation.
	 *
	 * @param message Validated progress payload.
	 * @param selection Optional normalized picker counts for the local batch.
	 */
	public observe(
		message: UploadProgressMessage,
		selection?: UploadBatchSelection,
	): void {
		if (!isUploadProgressMessageV2(message)) {
			return;
		}

		const identity = getUploadProgressIdentityKey(
			message.uploadSessionId,
			message.batchId,
		);

		if (this.terminalBatchIdentities.has(identity)) {
			return;
		}

		const state = this.getState(
			message,
			selection,
		);
		const now = this.now();

		const stageChanged = state.lastStage !== message.stage;

		getTransitionsProvenByStage(message.stage).forEach((transition) => {
			this.emitOnce(
				state,
				message,
				transition,
			);
		});

		if (stageChanged) {
			state.lastStage = message.stage;
			state.stageStartedAt = now;
		}

		if (
			message.stage === 'completed'
			|| message.stage === 'failed'
			|| message.stage === 'aborted'
		) {
			this.rememberTerminalBatch(message);
		}
	}

	/**
	 * Records a connection-recovery transition without sampling it out.
	 *
	 * @param message Last confirmed correlated progress payload.
	 * @param transition Recovery transition to emit.
	 */
	public recordRecovery(
		message: UploadProgressMessageV2,
		transition: 'recovering' | 'recovered',
	): void {
		const identity = getUploadProgressIdentityKey(
			message.uploadSessionId,
			message.batchId,
		);

		if (this.terminalBatchIdentities.has(identity)) {
			return;
		}

		const state = this.getState(message);

		getTransitionsProvenByStage(message.stage).forEach((provenTransition) => {
			state.emitted.add(provenTransition);
		});

		if (transition === 'recovering') {
			state.recoveryCount += 1;
			this.emitOnce(
				state,
				message,
				'stalled',
			);
		}

		this.emit(
			state,
			message,
			transition,
		);
	}

	/**
	 * Sends one sampled or always-retained lifecycle transition.
	 *
	 * @param state In-memory lifecycle state for the correlated batch.
	 * @param message Latest validated protocol-v2 payload.
	 * @param transition Transition being recorded.
	 */
	private emit(
		state: UploadLifecycleState,
		message: UploadProgressMessageV2,
		transition: UploadLifecycleTransition,
	): void {
		if (!state.sampled && !alwaysTrackTransitions.has(transition)) {
			return;
		}

		const now = this.now();
		this.options.track({
			appVersion: this.options.appVersion,
			batchId: message.batchId,
			elapsedStageMs: Math.max(
				0,
				now - state.stageStartedAt,
			),
			elapsedTotalMs: Math.max(
				0,
				now - state.totalStartedAt,
			),
			failureReason: message.stage === 'failed'
				? message.failureReason ?? 'unknown'
				: undefined,
			newCount: state.selection.newCount,
			platform: this.options.platform(),
			processedCount: message.processedCount,
			protocolVersion: 2,
			recoveryCount: state.recoveryCount,
			rejectedCount: state.selection.rejectedCount,
			selectedTotal: state.selection.selectedTotal,
			skippedExistingCount: state.selection.skippedExistingCount,
			source: message.source,
			stage: message.stage,
			submittedCount: message.submittedCount,
			transferredCount: message.transferredCount,
			transition,
			uploadSessionId: message.uploadSessionId,
		});
	}

	/**
	 * Prevents duplicate transition emission for repeated progress messages.
	 *
	 * @param state In-memory lifecycle state for the correlated batch.
	 * @param message Latest validated protocol-v2 payload.
	 * @param transition Transition that should occur at most once.
	 */
	private emitOnce(
		state: UploadLifecycleState,
		message: UploadProgressMessageV2,
		transition: UploadLifecycleTransition,
	): void {
		if (state.emitted.has(transition)) {
			return;
		}

		state.emitted.add(transition);
		this.emit(
			state,
			message,
			transition,
		);
	}

	/**
	 * Returns or initializes deterministic sampling/timing state for a batch.
	 *
	 * @param message Latest validated protocol-v2 payload.
	 * @param selection Optional normalized local picker counts.
	 * @returns Mutable lifecycle state retained for the upload session.
	 */
	private getState(
		message: UploadProgressMessageV2,
		selection?: UploadBatchSelection,
	): UploadLifecycleState {
		const identity = getUploadProgressIdentityKey(
			message.uploadSessionId,
			message.batchId,
		);
		const existing = this.states.get(identity);

		if (existing) {
			if (selection) {
				existing.selection = selection;
			}

			return existing;
		}

		const now = this.now();
		const state: UploadLifecycleState = {
			emitted: new Set(),
			lastStage: message.stage,
			recoveryCount: 0,
			sampled: shouldSampleUploadBatch(
				message.batchId,
				this.options.getSamplingRate(),
			),
			selection: selection ?? {
				batchId: message.batchId,
				newCount: message.submittedCount,
				rejectedCount: 0,
				selectedTotal: message.submittedCount,
				skippedExistingCount: 0,
				source: message.source,
			},
			stageStartedAt: now,
			totalStartedAt: now,
		};
		this.states.set(
			identity,
			state,
		);

		return state;
	}

	/** @returns Injected or wall-clock epoch milliseconds. */
	private now(): number {
		return this.options.now?.() ?? Date.now();
	}

	/**
	 * Releases detailed lifecycle state after termination while retaining a
	 * bounded recent-batch set that suppresses duplicate terminal delivery.
	 *
	 * @param message Correlated batch that reached a terminal stage.
	 */
	private rememberTerminalBatch(message: UploadProgressMessageV2): void {
		const identity = getUploadProgressIdentityKey(
			message.uploadSessionId,
			message.batchId,
		);

		this.states.delete(identity);
		this.terminalBatchIdentities.delete(identity);
		this.terminalBatchIdentities.add(identity);

		if (this.terminalBatchIdentities.size <= MAX_TERMINAL_UPLOAD_BATCHES) {
			return;
		}

		const oldestIdentity = this.terminalBatchIdentities.values().next().value;

		if (typeof oldestIdentity === 'string') {
			this.terminalBatchIdentities.delete(oldestIdentity);
		}
	}
}
